import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { normaliseCode, referralRewardText, rewardTier, SIGNUP_BONUS_POINTS, type BagLine, type LineConfig, type LineReward, type OrderDto, type PickupChoice, type PickupDay, type Offer, pickupAvailable, rollPickupForward } from '@tikkas/shared';
import { collectHappened, noteJustHappened } from './justHappened.js';
import { useNav } from './router.js';
import { closeAction, isHistorySheet, sheetInSearch, sheetPath, sheetSync } from './sheets.js';
import { api, ApiError } from './api.js';
import type { Config, Me, MenuPayload, CodeInfo } from './types.js';
import { newLine, rewardTarget, type Builder } from './bag.js';
import { track } from './events.js';
import { uid } from './format.js';
import { syncPush } from './push.js';
import { APP_BUILD, loadSessionToken, setSessionToken } from './platform.js';
import { fetchGuestOrder, guestRefLive, type GuestOrderRef } from './guest.js';
import { } from './look.js';

export type PayChoice = { kind: 'credit' } | { kind: 'saved'; id: string } | { kind: 'new' } | { kind: 'wallet' };

/* The customer's last explicit choice of how to pay, kept on the phone. "New card" is never a preference: once that card is
   remembered it is a saved card, and if it was not, the saved card or wallet is the better default next time. */
const PAY_PREF_KEY = 'tikkas-paywith';
export function rememberedPayChoice(): PayChoice | null {
  try {
    const raw = localStorage.getItem(PAY_PREF_KEY);
    return raw ? (JSON.parse(raw) as PayChoice) : null;
  } catch {
    return null;
  }
}
export function rememberPayChoice(c: PayChoice): void {
  try {
    if (c.kind === 'new') localStorage.removeItem(PAY_PREF_KEY);
    else localStorage.setItem(PAY_PREF_KEY, JSON.stringify(c));
  } catch {
    /* private mode: nothing to remember with */
  }
}
export type SheetState =
  | { kind: 'pickup' } | { kind: 'topup'; amountPence?: number } | { kind: 'credit' } | { kind: 'pickMain'; reward: LineReward } | { kind: 'pickSide'; reward: LineReward; dessertsOnly: boolean }
  | { kind: 'delete' } | { kind: 'bowl' } | { kind: 'saveOrder'; orderId: string } | { kind: 'spinRules' } | { kind: 'paying'; label: string } | { kind: 'name'; welcome?: boolean }
  | { kind: 'cardMenu' } | { kind: 'rewards' } | { kind: 'earn' } | { kind: 'code' } | { kind: 'emailList' }
  | { kind: 'notifications'; mode: 'ask' | 'offers'; place: 'home' | 'status' };
export type FxState = { kind: 'placed'; order: OrderDto } | { kind: 'collected'; from: number; to: number; earn: number; spinBonus: number; number: number; unlocked: boolean } | { kind: 'spinReveal'; orderId: string } | { kind: 'wheel'; orderId: string };

interface Persisted {
  bag: BagLine[];
  pickup: PickupChoice;
  code: CodeInfo | null;
  allergyNote: string;
  payWith: PayChoice;
  rememberCard: boolean;
  offline: { customerId: string; qrSecret: string; name: string } | null;
  /** Hearts given while signed out, kept on this phone until the next sign-in moves them to the account. */
  favourites: string[];
  /** Group order (docs/28): the bag holds this group's lines for the host to pay; cleared with the bag. */
  groupCheckout: GroupCheckout | null;
  /** Guest checkout (docs/30): orders placed without an account from this phone, newest first; forgotten once they join an account. */
  guestOrders: GuestOrderRef[];
}
export interface GroupCheckout { id: string; token: string; hostName: string }
const KEY = 'tikkas-app-v1';
function load(): Persisted {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { bag: [], pickup: { type: 'asap' }, code: null, allergyNote: '', payWith: { kind: 'new' }, rememberCard: true, offline: null, favourites: [], groupCheckout: null, guestOrders: [], ...(JSON.parse(raw) as Partial<Persisted>) };
  } catch {
    /* fresh */
  }
  return { bag: [], pickup: { type: 'asap' }, code: null, allergyNote: '', payWith: { kind: 'new' }, rememberCard: true, offline: null, favourites: [], groupCheckout: null, guestOrders: [] };
}

export interface Store extends Persisted {
  config: Config | null;
  menuData: MenuPayload | null;
  me: Me | null;
  meLoaded: boolean;
  pickupDays: PickupDay[];
  builder: Builder | null;
  sheet: SheetState | null;
  toastMsg: string | null;
  fx: FxState | null;
  notif: { title: string; body: string; orderId: string | null } | null;
  bump: boolean;
  codeError: string;
  bootError: string | null;
  /** A guest's live orders (docs/30), fetched by their links while signed out; empty once signed in (the account's liveOrders take over). */
  guestLive: OrderDto[];
  // actions
  addGuestOrder: (ref: GuestOrderRef) => void;
  forgetGuestOrder: (id: string) => void;
  refreshMe: () => Promise<Me | null>;
  signOut: () => Promise<void>;
  setMeFromSignIn: (me: Partial<Me> & { customer: Me['customer']; qrSecret: string }) => void;
  setBag: (lines: BagLine[]) => void;
  addLine: (config: LineConfig, qty?: number) => BagLine;
  updateLine: (id: string, config: LineConfig, qty: number) => void;
  lineQty: (id: string, delta: number) => void;
  removeLine: (id: string) => void;
  clearBag: () => void;
  setGroupCheckout: (g: GroupCheckout | null) => void;
  applyReward: (reward: LineReward, lineId?: string) => boolean;
  unreward: (rewardKind: string, rewardId?: string) => void;
  applyCode: (code: string) => Promise<boolean>;
  removeCode: () => void;
  setBuilder: (b: Builder | null | ((prev: Builder | null) => Builder | null)) => void;
  setPickup: (p: PickupChoice) => void;
  loadPickupDays: () => Promise<PickupDay[]>;
  setPayWith: (p: PayChoice) => void;
  setRememberCard: (v: boolean) => void;
  setAllergyNote: (s: string) => void;
  toggleFavourite: (itemId: string) => boolean;
  toast: (msg: string) => void;
  openSheet: (s: SheetState) => void;
  closeSheet: () => void;
  setFx: (f: FxState | null) => void;
  showNotif: (n: { title: string; body: string; orderId: string | null } | null) => void;
  setBump: (v: boolean) => void;
  offer: Offer | null;
}

const StoreContext = createContext<Store | null>(null);

const BOOT_KEY = 'tikkas-boot-v1';
/** The last config and menu the API gave, for opening the app when it can't be reached (never for prices). */
function keepBoot(config: Config, menu: MenuPayload): void {
  try {
    localStorage.setItem(BOOT_KEY, JSON.stringify({ config, menu, at: new Date().toISOString() }));
  } catch {
    /* private mode or full: the next boot just needs the API */
  }
}
function keptBoot(): { config: Config; menu: MenuPayload } | null {
  try {
    const raw = localStorage.getItem(BOOT_KEY);
    if (!raw) return null;
    const k = JSON.parse(raw) as { config?: Config; menu?: MenuPayload };
    return k.config && k.menu ? { config: k.config, menu: k.menu } : null;
  } catch {
    return null;
  }
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [p, setP] = useState<Persisted>(load);
  const [config, setConfig] = useState<Config | null>(null);
  const [menuData, setMenuData] = useState<MenuPayload | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [pickupDays, setPickupDays] = useState<PickupDay[]>([]);
  const [builder, setBuilderState] = useState<Builder | null>(null);
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const nav = useNav();
  const navRef = useRef(nav);
  navRef.current = nav;
  /** Open a sheet; the ones a customer can land on by themselves become a history entry (?sheet=kind), so Back returns to them (lib/sheets.ts). */
  const openSheet = useCallback((sh: SheetState) => {
    setSheet(sh);
    if (isHistorySheet(sh.kind) && sheetInSearch(new URLSearchParams(location.search)) !== sh.kind) navRef.current.navigate(sheetPath(location.pathname, sh.kind));
  }, []);
  /** Close the sheet showing; one that owns the address entry pops it (so a later Back doesn't reopen it). */
  const sheetRef = useRef<SheetState | null>(null);
  sheetRef.current = sheet;
  const closeSheet = useCallback(() => {
    const cur = sheetRef.current;
    setSheet(null);
    if (cur && closeAction(new URLSearchParams(location.search), cur.kind) === 'back') navRef.current.back();
  }, []);
  // The address names the sheet: Back (the left-edge swipe), a page pushed from a sheet, a reload or a deep link all keep the sheet in step.
  const sheetKey = nav.route.search.get('sheet');
  useEffect(() => {
    const what = sheetSync(nav.route.search, sheet?.kind ?? null);
    if (!what) return;
    if ('close' in what) setSheet(null);
    else setSheet({ kind: what.open } as SheetState);
  }, [nav.route.path, sheetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [fx, setFx] = useState<FxState | null>(null);
  const [notif, setNotif] = useState<Store['notif']>(null);
  const [bump, setBump] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [bootError, setBootError] = useState<string | null>(null);
  const bootFailure = useRef<string | null>(null);
  const [guestLive, setGuestLive] = useState<OrderDto[]>([]);
  const toastTimer = useRef<number | null>(null);
  const notifTimer = useRef<number | null>(null);
  const prevLive = useRef<Map<string, OrderDto>>(new Map());

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(p));
    } catch {
      /* private mode */
    }
  }, [p]);

  const pushSynced = useRef<string | null>(null);
  /** Hearts saved on this phone (signed out, or before favourites moved to the account): read by refreshMe to move them. */
  const localFavs = useRef(p.favourites);
  localFavs.current = p.favourites;
  const refreshMe = useCallback(async (): Promise<Me | null> => {
    try {
      let m = await api<Me>('/api/me');
      // Favourites live on the account (19 Oct 2026): hearts kept on this phone move there once, then the phone forgets them.
      if (localFavs.current.length) {
        const moved = await api<{ favourites: string[] }>('/api/me/favourites/import', { body: { itemIds: localFavs.current } }).catch(() => null);
        if (moved) {
          m = { ...m, favourites: moved.favourites };
          setP((s) => ({ ...s, favourites: [] }));
        }
      }
      setMe(m);
      // A device that already allowed notifications is tied to whoever is signed in on it (once per sign-in).
      if (pushSynced.current !== m.customer.id) {
        pushSynced.current = m.customer.id;
        syncPush().catch(() => undefined);
      }
      setP((s) => ({ ...s, offline: { customerId: m.customer.id, qrSecret: m.qrSecret, name: `${m.customer.firstName} ${m.customer.lastName}`.trim() } }));
      return m;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        if (APP_BUILD) await setSessionToken(null); // signed out elsewhere, expired or revoked
        setMe(null);
        setP((s) => ({ ...s, offline: null }));
      }
      return null;
    } finally {
      setMeLoaded(true);
    }
  }, []);

  // Boot: config, menu, me, pickup days. With the API unreachable (pre-launch audit, 25 Oct 2026) the app opens on the last
  // config and menu it had, says so, and keeps trying every 15 seconds; with nothing kept, the error page clears itself once
  // the API answers. Prices are never taken from the kept copy: the server works out every total.
  useEffect(() => {
    let live = true;
    let retry: number | undefined;
    const fetchBoot = async (): Promise<boolean> => {
      try {
        const [c, m] = await Promise.all([api<Config>('/api/config'), api<MenuPayload>('/api/menu')]);
        if (!live) return true;
        setConfig(c);
        setMenuData(m);
        setBootError(null);
        keepBoot(c, m);
        return true;
      } catch (e) {
        if (live) bootFailure.current = e instanceof Error ? e.message : 'Could not reach the Tikkas server';
        return false;
      }
    };
    const again = () => {
      retry = window.setTimeout(async () => {
        if (!live) return;
        if (await fetchBoot()) await refreshMe();
        else again();
      }, 15_000);
    };
    (async () => {
      await loadSessionToken(); // the app's saved sign-in (Keychain), before the first call
      if (!(await fetchBoot())) {
        const kept = keptBoot();
        if (kept) {
          setConfig(kept.config);
          setMenuData(kept.menu);
          toast("Can't reach Tikkas right now. Showing the menu we had; we'll keep trying.");
        } else setBootError(bootFailure.current ?? 'Could not reach the Tikkas server');
        again();
      }
      await refreshMe();
      track('app_opened');
    })();
    return () => { live = false; clearTimeout(retry); };
  }, [refreshMe]); // eslint-disable-line react-hooks/exhaustive-deps

  // A bag left behind (the app goes to the background with something in it) is an event the "abandoned bag" journey can use (doc 04 §5).
  const bagRef = useRef(p.bag);
  bagRef.current = p.bag;
  useEffect(() => {
    const on = () => {
      if (document.visibilityState !== 'hidden') return;
      const bag = bagRef.current;
      if (!bag.length) return;
      const sig = bag.map((l) => `${l.config.itemId}:${l.qty}`).join('|');
      try {
        if (sessionStorage.getItem('tikkas-bag-left') === sig) return;
        sessionStorage.setItem('tikkas-bag-left', sig);
      } catch {
        /* private mode: still record it */
      }
      track('bag_abandoned', { items: bag.reduce((a, l) => a + l.qty, 0) });
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  // Sold-out switches and price changes from the admin reach the app within about 30 seconds.
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') api<MenuPayload>('/api/menu').then(setMenuData).catch(() => undefined);
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  const loadPickupDays = useCallback(async () => {
    const r = await api<{ days: PickupDay[] }>('/api/pickup');
    setPickupDays(r.days);
    // A chosen time that has passed rolls forward to the earliest on offer, quietly (Saif, 22 Oct): the label says "Earliest collection".
    setP((p) => (pickupAvailable(p.pickup, r.days) ? p : { ...p, pickup: rollPickupForward(p.pickup, r.days) }));
    return r.days;
  }, []);
  useEffect(() => {
    loadPickupDays().catch(() => undefined);
    const t = window.setInterval(() => loadPickupDays().catch(() => undefined), 60_000);
    return () => clearInterval(t);
  }, [loadPickupDays]);

  // Live orders: poll, and turn status changes into the "ready" ping and the collection celebration.
  const liveIds = (me?.liveOrders ?? []).map((o) => o.id).join(',');
  useEffect(() => {
    if (!me) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshMe().catch(() => undefined);
    }, liveIds ? 8000 : 60000);
    return () => clearInterval(t);
  }, [me?.customer.id, liveIds, refreshMe]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!me) {
      prevLive.current = new Map();
      return;
    }
    const prev = prevLive.current;
    const nowLive = new Map(me.liveOrders.map((o) => [o.id, o] as const));
    for (const [id, old] of prev) {
      const cur = nowLive.get(id);
      if (cur && cur.status === 'ready' && old.status !== 'ready') showNotif({ title: 'Your order is ready', body: `Order #${cur.number} is at the counter. Show your code to collect.`, orderId: cur.id });
      if (!cur) {
        // Left the live list: collected (celebrate) or cancelled.
        api<{ order: OrderDto }>(`/api/me/orders/${id}`).then((r) => {
          if (r.order.status === 'collected' && !fx) {
            const earned = r.order.pointsEarned ?? 0;
            const spinBonus = r.order.spin.prize?.kind === 'points' ? r.order.spin.prize.points : 0;
            const to = me.customer.points;
            const from = to - earned;
            setFx({ kind: 'collected', from, to, earn: earned - spinBonus, spinBonus, number: r.order.number, unlocked: from < rewardPoints({ config: configRef.current }).main && to >= rewardPoints({ config: configRef.current }).main });
            noteJustHappened(collectHappened(earned, r.order.number));
          }
        }).catch(() => undefined);
      }
    }
    prevLive.current = nowLive;
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guest orders (docs/30): while signed out, each order kept on this phone is read by its link (every 8 s while one is on
  // the go), so Home's live row and the "ready" ping work for a guest too. A link that has died (joined an account, or gone)
  // is forgotten. Signed in, the account's own live orders take over.
  const guestRefs = p.guestOrders;
  const guestKey = guestRefs.map((g) => g.id).join(',');
  const prevGuest = useRef<Map<string, OrderDto>>(new Map());
  useEffect(() => {
    if (me || !guestRefs.length) {
      setGuestLive([]);
      prevGuest.current = new Map();
      return;
    }
    let live = true;
    const poll = async () => {
      const keep: OrderDto[] = [];
      for (const ref of guestRefs) {
        if (!guestRefLive(ref)) {
          setP((s) => ({ ...s, guestOrders: s.guestOrders.filter((g) => g.id !== ref.id) }));
          continue;
        }
        const r = await fetchGuestOrder(ref.id, ref.token).catch(() => null);
        if (!live) return;
        if (r === 'gone') setP((s) => ({ ...s, guestOrders: s.guestOrders.filter((g) => g.id !== ref.id) }));
        else if (r && (r.order.status === 'placed' || r.order.status === 'preparing' || r.order.status === 'ready')) keep.push(r.order);
      }
      if (!live) return;
      for (const o of keep) {
        const old = prevGuest.current.get(o.id);
        if (old && o.status === 'ready' && old.status !== 'ready') showNotif({ title: 'Your order is ready', body: `Order #${o.number} is at the counter. Show your code to collect.`, orderId: o.id });
      }
      prevGuest.current = new Map(keep.map((o) => [o.id, o] as const));
      setGuestLive(keep);
    };
    poll().catch(() => undefined);
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') poll().catch(() => undefined);
    }, 8000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [me?.customer.id, guestKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const addGuestOrder = useCallback((ref: GuestOrderRef) => setP((s) => ({ ...s, guestOrders: [ref, ...s.guestOrders.filter((g) => g.id !== ref.id)].slice(0, 10) })), []);
  const forgetGuestOrder = useCallback((id: string) => setP((s) => ({ ...s, guestOrders: s.guestOrders.filter((g) => g.id !== id) })), []);

  const configRef = useRef<Config | null>(null);
  configRef.current = config;
  const showClosed = useRef<(m: string) => void>(() => undefined);
  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastMsg(null), 2400);
  }, []);
  showClosed.current = toast;
  const showNotif = useCallback((n: Store['notif']) => {
    setNotif(n);
    if (notifTimer.current) clearTimeout(notifTimer.current);
    if (n) notifTimer.current = window.setTimeout(() => setNotif(null), 5000);
  }, []);

  const setBag = useCallback((lines: BagLine[]) => setP((s) => ({ ...s, bag: lines })), []);
  const addLine = useCallback((config: LineConfig, qty = 1) => {
    const line = newLine(config, qty);
    // Online ordering off: nothing goes in the bag (the server would refuse the order anyway).
    if (configRef.current?.ordering?.online === false) {
      showClosed.current(configRef.current.ordering.message ?? 'Online ordering opens soon');
      return line;
    }
    setP((s) => ({ ...s, bag: [...s.bag, line] }));
    setBump(true);
    window.setTimeout(() => setBump(false), 1600);
    track('added_to_bag', { itemId: config.itemId, qty });
    return line;
  }, []);
  const updateLine = useCallback((id: string, config: LineConfig, qty: number) => setP((s) => ({ ...s, bag: s.bag.map((l) => (l.id === id ? { ...l, config, qty } : l)) })), []);
  const lineQty = useCallback((id: string, delta: number) => setP((s) => ({ ...s, bag: s.bag.map((l) => (l.id === id ? { ...l, qty: l.qty + delta } : l)).filter((l) => l.qty > 0) })), []);
  const removeLine = useCallback((id: string) => setP((s) => ({ ...s, bag: s.bag.filter((l) => l.id !== id) })), []);
  const clearBag = useCallback(() => setP((s) => ({ ...s, bag: [], code: null, allergyNote: '', groupCheckout: null })), []);
  const setGroupCheckout = useCallback((groupCheckout: GroupCheckout | null) => setP((s) => ({ ...s, groupCheckout })), []);

  const applyReward = useCallback((reward: LineReward, lineId?: string): boolean => {
    if (!menuData) return false;
    const menu = menuData.menu;
    const rid = reward.kind === 'points' ? reward.rewardId : reward.kind === 'spin' ? 'spin' : null;
    if (!rid) return false;
    let applied = false;
    setP((s) => {
      const target = lineId ? s.bag.find((l) => l.id === lineId) : rewardTarget(menu, s.bag, rid);
      if (!target) return s;
      applied = true;
      const bag = [...s.bag];
      const idx = bag.indexOf(target);
      if (target.qty > 1) {
        bag[idx] = { ...target, qty: target.qty - 1 };
        bag.splice(idx + 1, 0, { id: uid(), qty: 1, config: { ...target.config, reward } });
      } else bag[idx] = { ...target, config: { ...target.config, reward } };
      return { ...s, bag };
    });
    track('reward_used', { reward });
    return applied;
  }, [menuData]);
  const unreward = useCallback((rewardKind: string, rewardId?: string) => {
    setP((s) => ({ ...s, bag: s.bag.map((l) => (l.config.reward && l.config.reward.kind === rewardKind && (!rewardId || (l.config.reward.kind === 'points' && l.config.reward.rewardId === rewardId)) ? { ...l, config: { ...l.config, reward: null } } : l)) }));
  }, []);

  const applyCode = useCallback(async (input: string): Promise<boolean> => {
    const code = normaliseCode(input);
    if (!code) {
      setCodeError('Type a code first.');
      return false;
    }
    try {
      const r = await api<{ ok: boolean; message?: string; code?: CodeInfo }>('/api/codes/check', { body: { code } });
      if (!r.ok || !r.code) {
        setCodeError(r.message ?? "That code isn't valid.");
        return false;
      }
      const info = r.code;
      setCodeError('');
      setP((s) => {
        const bag = s.bag.filter((l) => l.config.reward?.kind !== 'code');
        if (info.offer.type === 'free_item') bag.push(newLine({ itemId: info.offer.itemId, sizeIndex: 0, portions: [], sauces: [], toppingsOff: [], upgradeIds: [], reward: { kind: 'code', code: info.code } }));
        return { ...s, bag, code: info };
      });
      toast(info.label);
      return true;
    } catch (e) {
      setCodeError(e instanceof Error ? e.message : 'Could not check that code');
      return false;
    }
  }, [toast]);
  const removeCode = useCallback(() => {
    setCodeError('');
    setP((s) => ({ ...s, code: null, bag: s.bag.filter((l) => l.config.reward?.kind !== 'code') }));
  }, []);

  const setBuilder = useCallback((b: Builder | null | ((prev: Builder | null) => Builder | null)) => setBuilderState((prev) => (typeof b === 'function' ? b(prev) : b)), []);
  const setPickup = useCallback((pickup: PickupChoice) => setP((s) => ({ ...s, pickup })), []);
  const setPayWith = useCallback((payWith: PayChoice) => setP((s) => ({ ...s, payWith })), []);
  const setRememberCard = useCallback((rememberCard: boolean) => setP((s) => ({ ...s, rememberCard })), []);
  const setAllergyNote = useCallback((allergyNote: string) => setP((s) => ({ ...s, allergyNote })), []);
  const meRef = useRef(me);
  meRef.current = me;
  /**
   * Heart or un-heart an item; returns whether it is now a favourite. Signed in it is saved on the account (shown at once,
   * then the server's list); signed out it waits on this phone and moves to the account at the next sign-in.
   */
  const toggleFavourite = useCallback((itemId: string): boolean => {
    const cur = meRef.current;
    const list = cur ? cur.favourites ?? [] : localFavs.current;
    const on = !list.includes(itemId);
    const next = on ? [itemId, ...list.filter((x) => x !== itemId)] : list.filter((x) => x !== itemId);
    if (cur) {
      setMe((m) => (m ? { ...m, favourites: next } : m));
      api<{ favourites: string[] }>(`/api/me/favourites/${encodeURIComponent(itemId)}`, { method: on ? 'PUT' : 'DELETE' })
        .then((r) => setMe((m) => (m ? { ...m, favourites: r.favourites } : m)))
        .catch(() => setMe((m) => (m ? { ...m, favourites: list } : m)));
    } else setP((s) => ({ ...s, favourites: next.slice(0, 24) }));
    if (on) track('item_favourited', { itemId });
    return on;
  }, []);
  const signOut = useCallback(async () => {
    await api('/api/auth/signout', { body: {} }).catch(() => undefined);
    await setSessionToken(null);
    setMe(null);
    setP((s) => ({ ...s, offline: null, bag: s.groupCheckout ? [] : s.bag.filter((l) => !l.config.reward), groupCheckout: null, code: null, payWith: { kind: 'new' } }));
    track('signed_out');
  }, []);
  const setMeFromSignIn = useCallback((m: Partial<Me> & { customer: Me['customer']; qrSecret: string }) => {
    setMe({ liveOrders: [], lastOrder: null, savedOrders: [], unseenMessages: 0, ...m });
    setMeLoaded(true);
    setP((s) => ({ ...s, offline: { customerId: m.customer.id, qrSecret: m.qrSecret, name: `${m.customer.firstName} ${m.customer.lastName}`.trim() } }));
    refreshMe().catch(() => undefined);
  }, [refreshMe]);

  const offer = useMemo<Offer | null>(() => (p.code && p.code.offer.type === 'amount_off' ? { type: 'amount_off', pence: p.code.offer.pence, appliesTo: 'main' } : null), [p.code]);

  const value: Store = {
    ...p, favourites: me ? me.favourites ?? [] : p.favourites, config, menuData, me, meLoaded, pickupDays, builder, sheet, toastMsg, fx, notif, bump, codeError, bootError, offer, guestLive,
    addGuestOrder, forgetGuestOrder, refreshMe, signOut, setMeFromSignIn, setBag, addLine, updateLine, lineQty, removeLine, clearBag, setGroupCheckout, applyReward, unreward, applyCode, removeCode, setBuilder, setPickup, loadPickupDays,
    setPayWith, setRememberCard, setAllergyNote, toggleFavourite, toast, openSheet, closeSheet, setFx, showNotif, setBump,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const s = useContext(StoreContext);
  if (!s) throw new Error('StoreProvider missing');
  return s;
}

export type Feature = 'catering' | 'groupOrder' | 'games' | 'wallet' | 'levels' | 'spin' | 'guestCheckout';
/** A switchable feature (Admin → Settings → Features). Off unless the server says on, so a feature never shows by accident. */
export function featureOn(s: Pick<Store, 'config'>, f: Feature): boolean {
  if (f === 'spin') return s.config?.spin?.enabled === true || s.config?.features?.spin === true;
  return s.config?.features?.[f] === true;
}

/** Online ordering is open (Admin → Settings). Unknown (still loading) counts as open; the server has the last word. */
export function orderingOpen(s: Pick<Store, 'config'>): boolean {
  return s.config?.ordering?.online !== false;
}

/** The Rewards Club numbers Saif set (Admin → Settings), with the launch numbers until the config has loaded. */
export function rewardPoints(s: Pick<Store, 'config'>): { side: number; main: number; signup: number } {
  const tiers = s.config?.rewards.tiers;
  return { side: rewardTier('side', tiers).points, main: rewardTier('main', tiers).points, signup: s.config?.rewards.signupBonus ?? SIGNUP_BONUS_POINTS };
}

/**
 * The welcome points still to come (docs/23 §3.6): a new member's land with their first collected purchase, so until
 * then the app says they're on their way. 0 when they've landed, for an older account, or when the welcome is set to 0.
 */
export function welcomeToCome(s: Pick<Store, 'config' | 'me'>): number {
  return s.me?.customer.welcomeGiven === false ? rewardPoints(s).signup : 0;
}

/** Points for confirming an email, while the account hasn't had them (0 = no prompt). */
export function emailBonusToCome(s: Pick<Store, 'config' | 'me'>): number {
  return s.me && !s.me.customer.emailBonusEarned ? s.config?.joining?.emailBonusPoints ?? 0 : 0;
}

/** The item behind a 'referral_item' voucher when the voucher doesn't say (the referral offer was a free item then). */
export function referralName(s: Pick<Store, 'config'>): string {
  return s.config?.referral?.name ?? 'Tikki Tots';
}
/** What both sides get for a referral, as words: "100 points" or "free Tikki Tots" (set on the referral offer in the admin). */
export function referralWhat(s: Pick<Store, 'config'>): string {
  return referralRewardText(s.config?.referral);
}

/** "Chips, Tikki Tots, Gulab Jamun or Rasgulla": what the free side reward can be today (hidden items are not on the menu). */
export function freeSideChoices(s: Pick<Store, 'menuData'>): string {
  const menu = s.menuData?.menu;
  if (!menu) return 'A Regular side or a dessert';
  const names = Object.values(menu.items).filter((i) => (i.category === 'sides' || i.category === 'desserts') && !i.soldOut).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((i) => i.name);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0] ?? 'A Regular side or a dessert';
}

/** Free (unreserved) points in the Rewards Club. */
export function freePoints(s: Store): number {
  const reserved = s.bag.reduce((a, l) => a + (l.config.reward?.kind === 'points' ? rewardTier(l.config.reward.rewardId, s.config?.rewards.tiers).points * l.qty : 0), 0);
  return (s.me?.customer.points ?? 0) - reserved;
}
